import type { Action, Subjects } from '@twinepos/vue-rbac'

import type { MenuSection } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'
import type { ActionCategory } from '@/domains/command-center/types'

import { getBillCategory } from '@/domains/command-center/handlers/bills/billCommands'
import { getNavigationCategory } from '@/domains/command-center/handlers/navigation/navigationCommands'
import { getProductCategory } from '@/domains/command-center/handlers/product/productCommands'
import { getQuickUpdateCategory } from '@/domains/command-center/handlers/quick-updates/quickUpdateCommands'
import { getStockCategory } from '@/domains/command-center/handlers/stock/stockCommands'

/**
 * Central registry of all command categories.
 *
 * `navSections` must be the sections `useMenuStore` has already authorised. They
 * are passed in rather than read here so this stays a plain function, and so the
 * generator built on it stays testable without a store.
 */
const getAllCategories = (navSections: MenuSection[] = []): ActionCategory[] => {
  return [
    getProductCategory(),
    getBillCategory(),
    getStockCategory(),
    getQuickUpdateCategory(),
    // Pages last: the curated actions above answer a verb ("create a bill"), and
    // this answers a destination. Add new categories above this one.
    getNavigationCategory(navSections)
  ]
}

/**
 * What a row says when the user's role does not allow it. It rides on the row rather than sitting
 * in a tooltip, because a muted control with no reason reads as a bug.
 */
const LOCKED_REASON = 'You do not have permission to do this.'

/**
 * Whether the user may use a category: they hold its permission, or every permission in its list. A
 * category with none is open, because the server has already withheld the sections behind it.
 *
 * `can` is passed in, so this stays a plain function like the rest of the registry.
 */
const isUsable = (category: ActionCategory, can: (action: Action, subject: Subjects) => boolean): boolean =>
  [category.permission ?? []].flat().every(({ action, subject }) => can(action, subject))

/**
 * A category the user may not use, shown rather than hidden.
 *
 * Every action reads muted and refuses selection — the palette's own pattern for a command that is
 * present but not available, which is how "Add Variants" already reads before a barcode arrives.
 * Hiding them instead would leave the palette looking different per user with nothing to explain
 * why, and a permission is something an owner can grant, so the answer is "not yet", not "never".
 */
const lockCategory = (category: ActionCategory): ActionCategory => ({
  ...category,
  isLocked: true,
  actions: category.actions.map(action => ({
    ...action,
    disabled: true,
    subLabel: LOCKED_REASON
  }))
})

export { getAllCategories, isUsable, lockCategory, LOCKED_REASON }
